import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button, Space } from 'antd';
import { CameraOutlined, StopOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';
import {
  isBarcodeDetectorSupported,
  createDataMatrixDetector,
} from '../../utils/barcodeDetector';
import { notifyScanSuccess } from '../../utils/scanFeedback';

interface Props {
  onScan: (code: string) => void;
}

// Ref-API сканера. Родительский компонент может дёрнуть restart() — например
// из центральной кнопки mobile bottom bar, если камера зависла.
export interface DataMatrixScannerHandle {
  restart: () => Promise<void>;
}

const SCANNER_ID = 'dm-scanner';
const COOLDOWN_MS = 2500;

type ScanStatus = 'idle' | 'ready' | 'scanned';

const STATUS_STYLES: Record<ScanStatus, { bg: string; color: string; border: string; text: string }> = {
  idle:    { bg: '#f0f0f0', color: '#999',    border: '#d9d9d9', text: 'Камера выключена' },
  ready:   { bg: '#f6ffed', color: '#52c41a', border: '#52c41a', text: '● Готов к сканированию' },
  scanned: { bg: '#e6f4ff', color: '#1677ff', border: '#1677ff', text: '✓ Отсканировано' },
};

const ERROR_BORDER = '#ff4d4f';

const DataMatrixScanner = forwardRef<DataMatrixScannerHandle, Props>(({ onScan }, ref) => {
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState<ScanStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const detectorRef = useRef<ReturnType<typeof createDataMatrixDetector> | null>(null);
  const useNativeRef = useRef(false);

  const html5Ref = useRef<Html5Qrcode | null>(null);

  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDetected = useCallback((text: string) => {
    if (cooldownRef.current) return;
    cooldownRef.current = true;
    setStatus('scanned');
    notifyScanSuccess();
    onScan(text);
    cooldownTimerRef.current = setTimeout(() => {
      cooldownRef.current = false;
      setStatus('ready');
    }, COOLDOWN_MS);
  }, [onScan]);

  const applyTorch = useCallback(async (on: boolean) => {
    const track = streamRef.current?.getVideoTracks()?.[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as any] });
      setTorchOn(on);
    } catch {
      // часть устройств не даёт менять torch на лету
    }
  }, []);

  const stopNative = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    detectorRef.current = null;
    setTorchSupported(false);
    setTorchOn(false);
  }, []);

  const stopFallback = useCallback(async () => {
    try {
      if (html5Ref.current?.isScanning) await html5Ref.current.stop();
    } catch {}
    html5Ref.current = null;
  }, []);

  const stop = useCallback(async () => {
    if (useNativeRef.current) stopNative();
    else await stopFallback();
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownRef.current = false;
    setScanning(false);
    setStatus('idle');
  }, [stopNative, stopFallback]);

  const startNative = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: 'continuous' } as any],
      },
      audio: false,
    });
    streamRef.current = stream;

    const track = stream.getVideoTracks()[0];
    const caps: any = track.getCapabilities?.() || {};
    setTorchSupported(!!caps.torch);

    const video = document.createElement('video');
    video.setAttribute('playsinline', 'true');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    videoRef.current = video;

    const container = document.getElementById(SCANNER_ID);
    if (container) {
      container.innerHTML = '';
      video.style.width = '100%';
      video.style.height = 'auto';
      video.style.display = 'block';
      container.appendChild(video);
    }

    detectorRef.current = createDataMatrixDetector();

    const loop = async () => {
      if (!videoRef.current || !detectorRef.current) return;
      try {
        const results = await detectorRef.current.detect(videoRef.current);
        if (results.length > 0) {
          handleDetected(results[0].rawValue);
        }
      } catch {
        // тихо игнорируем паузы и кадры без детектов
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [handleDetected]);

  const startFallback = useCallback(async () => {
    const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false });
    html5Ref.current = scanner;

    let source: string | { facingMode: string } = { facingMode: 'environment' };
    try {
      const cams = await Html5Qrcode.getCameras();
      if (cams?.length) {
        const rxBack = /back|rear|environment|задн/i;
        const rxBad = /wide|ultra|tele|macro|depth|bokeh|широк|телеф/i;
        const backs = cams.filter((c) => rxBack.test(c.label));
        const pool = backs.length ? backs : cams;
        const main = pool.find((c) => !rxBad.test(c.label)) ?? pool[0];
        source = main.id;
      }
    } catch {}

    await scanner.start(
      source as any,
      {
        fps: 25,
        qrbox: { width: 300, height: 300 },
        videoConstraints: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          advanced: [{ focusMode: 'continuous' } as any],
        } as any,
      },
      handleDetected,
      () => {},
    );

    const videoEl = document.querySelector(`#${SCANNER_ID} video`) as HTMLVideoElement | null;
    const stream = (videoEl?.srcObject as MediaStream | null) ?? null;
    streamRef.current = stream;
    const track = stream?.getVideoTracks()?.[0];
    const caps: any = track?.getCapabilities?.() || {};
    setTorchSupported(!!caps.torch);
  }, [handleDetected]);

  const start = useCallback(async () => {
    setError(null);
    try {
      const native = await isBarcodeDetectorSupported();
      useNativeRef.current = native;
      if (native) await startNative();
      else await startFallback();
      setScanning(true);
      setStatus('ready');
    } catch (e: any) {
      setError(e?.message || 'Не удалось запустить камеру');
    }
  }, [startNative, startFallback]);

  useEffect(() => {
    start();
    const onVis = () => {
      if (document.hidden) {
        stop();
      } else if (!scanning) {
        start();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
    // eslint-disable-next-line
  }, []);

  useImperativeHandle(ref, () => ({
    restart: async () => {
      await stop();
      await start();
    },
  }), [stop, start]);

  const st = STATUS_STYLES[status];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        {!scanning ? (
          <Button type="primary" icon={<CameraOutlined />} onClick={start}>
            Сканировать
          </Button>
        ) : (
          <Button danger icon={<StopOutlined />} onClick={stop}>
            Остановить
          </Button>
        )}
        {scanning && torchSupported && (
          <Button
            icon={torchOn ? <BulbFilled /> : <BulbOutlined />}
            onClick={() => applyTorch(!torchOn)}
          >
            {torchOn ? 'Выкл. фонарик' : 'Фонарик'}
          </Button>
        )}
      </Space>

      <div style={{
        padding: '8px 16px',
        marginBottom: 12,
        borderRadius: 8,
        background: st.bg,
        color: st.color,
        fontWeight: 600,
        fontSize: 15,
        textAlign: 'center',
        transition: 'all 0.3s ease',
      }}>
        {st.text}
      </div>

      {error && <div style={{ color: '#ff4d4f', marginBottom: 12 }}>{error}</div>}
      <div
        id={SCANNER_ID}
        style={{
          maxWidth: 500,
          border: `3px solid ${error ? ERROR_BORDER : st.border}`,
          borderRadius: 8,
          overflow: 'hidden',
          transition: 'border-color 0.25s ease',
        }}
      />
    </div>
  );
});

DataMatrixScanner.displayName = 'DataMatrixScanner';

export default DataMatrixScanner;
